import React, { useState, useEffect } from 'react';
import { format } from 'date-fns';
import { Clock, User, Eye, Edit } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { SessionBookingWithMentee } from '@/utils/sessionBookingsUtils';
import { AvailabilitySlot, createOrUpdateDateSpecificAvailability, getDateSpecificAvailability } from '@/utils/availabilityUtils';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/hooks/useAuth';

interface DayDetailModalProps {
  date: Date | null;
  isOpen: boolean;
  onClose: () => void;
  bookings: SessionBookingWithMentee[];
  availability: AvailabilitySlot[];
  onBookingClick: (booking: SessionBookingWithMentee) => void;
  onAvailabilityChanged?: () => void;
}

const DayDetailModal = ({ 
  date, 
  isOpen, 
  onClose, 
  bookings, 
  availability, 
  onBookingClick,
  onAvailabilityChanged 
}: DayDetailModalProps) => {
  const [hoveredSlot, setHoveredSlot] = useState<string | null>(null);
  const [isUpdating, setIsUpdating] = useState<string | null>(null);
  const [dateSpecificAvailability, setDateSpecificAvailability] = useState<any[]>([]);
  const { toast } = useToast();
  const { user } = useAuth();

  // Load date-specific availability when modal opens
  useEffect(() => {
    if (isOpen && date && user?.id) {
      const loadDateSpecificAvailability = async () => {
        const dateString = format(date, 'yyyy-MM-dd');
        const result = await getDateSpecificAvailability(user.id, dateString);
        if (result.success) {
          setDateSpecificAvailability(result.data || []);
        }
      };
      loadDateSpecificAvailability();
    }
  }, [isOpen, date, user?.id]);

  // Move the early return after all hooks
  if (!date) return null;

  // Generate hourly slots from 7 AM to 9 PM
  const generateTimeSlots = () => {
    const slots = [];
    for (let hour = 7; hour <= 21; hour++) {
      const time = `${hour.toString().padStart(2, '0')}:00`;
      slots.push(time);
    }
    return slots;
  };

  const timeSlots = generateTimeSlots();

  // Helper function to convert time string to minutes for positioning
  const timeToMinutes = (timeStr: string) => {
    const [hours, minutes] = timeStr.split(':').map(Number);
    return hours * 60 + minutes;
  };

  // Helper function to check if a booking overlaps with a time slot
  const getBookingForSlot = (time: string) => {
    return bookings.find(booking => {
      const slotTime = timeToMinutes(time);
      const startTime = timeToMinutes(booking.start_time);
      const endTime = timeToMinutes(booking.end_time);
      return slotTime >= startTime && slotTime < endTime;
    });
  };

  // Helper function to check if mentor is available at this time
  const isAvailableAtTime = (time: string) => {
    const slotTime = timeToMinutes(time);
    
    // First check if there's date-specific availability that overrides the default
    const hasDateSpecificOverride = dateSpecificAvailability.some(slot => {
      const startTime = timeToMinutes(slot.start_time);
      const endTime = timeToMinutes(slot.end_time);
      return slotTime >= startTime && slotTime < endTime;
    });
    
    // If there's a date-specific override, use it
    if (hasDateSpecificOverride) {
      return dateSpecificAvailability.some(slot => {
        const startTime = timeToMinutes(slot.start_time);
        const endTime = timeToMinutes(slot.end_time);
        return slotTime >= startTime && slotTime < endTime && slot.is_active;
      });
    }
    
    // Fall back to weekly recurring availability
    const dayOfWeek = date.getDay();
    return availability.some(slot => {
      if (slot.day_of_week !== dayOfWeek || !slot.is_active) return false;
      const startTime = timeToMinutes(slot.start_time);
      const endTime = timeToMinutes(slot.end_time);
      return slotTime >= startTime && slotTime < endTime;
    });
  };

  // Handle availability change for a specific time slot on a specific date
  const handleChangeAvailability = async (time: string) => {
    if (!user?.id || !date) return;
    
    const isCurrentlyAvailable = isAvailableAtTime(time);
    const dateString = format(date, 'yyyy-MM-dd');
    
    setIsUpdating(time);
    
    try {
      // Get current date-specific availability
      const currentResult = await getDateSpecificAvailability(user.id, dateString);
      let currentSlots = currentResult.success ? currentResult.data! || [] : [];
      
      const slotTime = timeToMinutes(time);
      const endTime = slotTime + 60; // 1 hour slots
      const endTimeString = `${Math.floor(endTime / 60).toString().padStart(2, '0')}:${(endTime % 60).toString().padStart(2, '0')}`;
      
      // If there's no date-specific availability yet, create it based on current weekly pattern
      if (currentSlots.length === 0) {
        const dayOfWeek = date.getDay();
        const weeklyAvailability = availability.filter(slot => 
          slot.day_of_week === dayOfWeek && slot.is_active
        );
        
        // Convert weekly availability to date-specific slots
        weeklyAvailability.forEach(slot => {
          currentSlots.push({
            start_time: slot.start_time,
            end_time: slot.end_time,
            is_active: true
          });
        });
      }
      
      if (isCurrentlyAvailable) {
        // Remove availability for this specific hour
        const newSlots: any[] = [];
        
        currentSlots.forEach(slot => {
          const startTime = timeToMinutes(slot.start_time);
          const endTime = timeToMinutes(slot.end_time);
          
          // If slot doesn't overlap with the hour we're removing, keep it
          if (!(startTime < slotTime + 60 && endTime > slotTime)) {
            newSlots.push(slot);
          } else {
            // Split the slot around the removed hour
            // Add slot before the removed hour
            if (startTime < slotTime) {
              newSlots.push({
                start_time: slot.start_time,
                end_time: time,
                is_active: true
              });
            }
            
            // Add slot after the removed hour
            if (endTime > slotTime + 60) {
              newSlots.push({
                start_time: endTimeString,
                end_time: slot.end_time,
                is_active: true
              });
            }
          }
        });
        
        currentSlots = newSlots;
      } else {
        // Add availability for this specific hour
        let slotAdded = false;
        
        // Try to merge with adjacent slots
        currentSlots = currentSlots.map(slot => {
          const startTime = timeToMinutes(slot.start_time);
          const endTime = timeToMinutes(slot.end_time);
          
          // Extend existing slot if adjacent
          if (endTime === slotTime) {
            slotAdded = true;
            return {
              ...slot,
              end_time: endTimeString
            };
          }
          
          if (startTime === slotTime + 60) {
            slotAdded = true;
            return {
              ...slot,
              start_time: time
            };
          }
          
          return slot;
        });
        
        // If we couldn't merge, create a new slot
        if (!slotAdded) {
          currentSlots.push({
            start_time: time,
            end_time: endTimeString,
            is_active: true
          });
        }
        
        // Sort slots by start time
        currentSlots.sort((a, b) => timeToMinutes(a.start_time) - timeToMinutes(b.start_time));
      }
      
      // Update date-specific availability
      const updateResult = await createOrUpdateDateSpecificAvailability(
        user.id, 
        dateString, 
        currentSlots
      );
      
      if (!updateResult.success) {
        throw new Error('Failed to update date-specific availability');
      }
      
      // Refresh the date-specific availability
      const refreshResult = await getDateSpecificAvailability(user.id, dateString);
      if (refreshResult.success) {
        setDateSpecificAvailability(refreshResult.data || []);
      }
      
      const action = isCurrentlyAvailable ? 'removed' : 'added';
      toast({
        title: "Availability Updated",
        description: `Successfully ${action} availability for ${time} on ${format(date, 'EEEE, MMMM d')}. This change only affects this specific date.`,
      });
      
      // Notify parent component to refresh data
      if (onAvailabilityChanged) {
        onAvailabilityChanged();
      }
      
    } catch (error) {
      console.error('Error updating availability:', error);
      toast({
        title: "Update Failed",
        description: "Failed to update availability. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsUpdating(null);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-md max-h-[80vh] overflow-hidden flex flex-col !rounded-none">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Clock className="h-5 w-5 text-emu-blue" />
            {format(date, 'EEEE, MMMM d, yyyy')}
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto py-4">
          <div className="space-y-1">
            {timeSlots.map((time) => {
              const booking = getBookingForSlot(time);
              const isAvailable = isAvailableAtTime(time);
              const slotKey = `${date.toDateString()}-${time}`;
              const isUpdatingThisSlot = isUpdating === time;

              // Determine background color
              let bgColor = 'bg-red-100'; // Default unavailable
              let textColor = 'text-gray-600';
              
              if (booking) {
                bgColor = 'bg-blue-100';
                textColor = 'text-blue-800';
              } else if (isAvailable) {
                bgColor = 'bg-green-100';
                textColor = 'text-green-700';
              }

              return (
                <div
                  key={time}
                  className={`flex items-center gap-3 p-3 h-12 border border-gray-200 ${bgColor} ${textColor}`}
                  onMouseEnter={() => setHoveredSlot(slotKey)}
                  onMouseLeave={() => setHoveredSlot(null)}
                >
                  <div className="w-16 text-sm font-medium flex-shrink-0">
                    {time}
                  </div>

                  <div className="flex-1 min-w-0">
                    {booking ? (
                      <div className="flex items-center justify-between gap-2">
                        <div className="flex items-center gap-2 min-w-0 flex-1">
                          <User className="h-3 w-3 flex-shrink-0" />
                          <span className="text-sm truncate">
                            {booking.mentee.first_name} {booking.mentee.last_name}
                          </span>
                        </div>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-6 px-2 text-xs text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                          onClick={() => onBookingClick(booking)}
                        >
                          <Eye className="h-3 w-3 mr-1" />
                          View Details
                        </Button>
                      </div>
                    ) : (
                      <div className="flex items-center justify-between">
                        <span className="text-sm">
                          {isAvailable ? 'Available' : 'Unavailable'}
                        </span>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-6 px-2 text-xs hover:bg-gray-50"
                          onClick={() => handleChangeAvailability(time)}
                          disabled={isUpdatingThisSlot}
                        >
                          <Edit className="h-3 w-3 mr-1" />
                          {isUpdatingThisSlot ? 'Updating...' : 'Change'}
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="border-t pt-4 mt-4">
          <div className="flex flex-wrap gap-4 text-xs">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 bg-blue-100 border border-blue-200"></div>
              <span className="text-gray-600">Booked</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 bg-green-100 border border-green-200"></div>
              <span className="text-gray-600">Available</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 bg-red-100 border border-red-200"></div>
              <span className="text-gray-600">Unavailable</span>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default DayDetailModal;
